import { useState } from 'react';
import { Badge, Dropdown, Avatar } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useErrorMessage } from '../../utils/errorMessage';
import { useAuthContext } from '../../contexts/AuthContext';
import { handleNotifMenuItemLink } from './handleNotificationMenuLink';

const MAX_NOTIFICATIONS = 5;

const StyledPanel = styled.div`
  width: 380px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  background: var(--ds-surface);
  border-radius: 14px;
  box-shadow: var(--ds-shadow-popover);

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--ds-row-divider);
    font-size: 15px;
    font-weight: 700;
  }

  .panel-link {
    padding: 0;
    border: none;
    background: transparent;
    font-size: 13px;
    font-weight: 600;
    color: var(--ds-cambridge-dark);
    cursor: pointer;

    &:hover {
      color: var(--darkGray);
    }
  }

  .panel-empty {
    padding: 28px 20px;
    text-align: center;
    font-size: 13px;
    color: var(--dimGray);
  }

  .panel-footer {
    display: block;
    width: 100%;
    padding: 14px;
    text-align: center;
  }
`;

export const Notifications = ({ notifications }) => {
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI, user } = useAuthContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const displayable = (notifications || [])
    .filter((notification) => notification.item?.type !== null)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const unread = displayable.filter(
    (notification) => !notification.consulted?.includes(user._id)
  );

  const markAllAsRead = async () => {
    try {
      await Promise.all(
        unread.map(({ _id }) =>
          dispatchAPI('PATCH', { url: `notifications/${_id}` })
        )
      );
    } catch (e) {
      message(e);
    }
  };

  const panel = () => (
    <StyledPanel>
      <div className="panel-header">
        <span>{t('notifications.title')}</span>
        {unread.length > 0 && (
          <button type="button" className="panel-link" onClick={markAllAsRead}>
            {t('notifications.mark_all_as_read')}
          </button>
        )}
      </div>
      {displayable.length === 0 ? (
        <div className="panel-empty">{t('notifications.no_alert')}</div>
      ) : (
        displayable.slice(0, MAX_NOTIFICATIONS).map((notification) => (
          <div key={notification._id}>
            {handleNotifMenuItemLink(
              notification._id,
              () => {},
              t,
              notification.created_at,
              notification.consulted,
              notification.item,
              'row',
              () => setOpen(false)
            )}
          </div>
        ))
      )}
      <button
        type="button"
        className="panel-link panel-footer"
        onClick={() => {
          setOpen(false);
          navigate('/');
        }}
      >
        {t('notifications.see_all')}
      </button>
    </StyledPanel>
  );

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      dropdownRender={panel}
      placement="bottomRight"
    >
      <Badge
        count={unread.length}
        size="small"
        className="notification-badge"
        offset={[0, 0]}
      >
        <Avatar icon={<BellOutlined />} />
      </Badge>
    </Dropdown>
  );
};

Notifications.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      item: PropTypes.shape({
        type: PropTypes.shape({
          _id: PropTypes.string
        })
      })
    })
  )
};

Notifications.defaultProps = {
  notifications: null
};
